import { Divider, Group, Progress, ScrollArea, Stack } from '@mantine/core';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { getCurrency } from '../../../../../helpers/currency-helpers';
import { formatMonthDate } from '../../../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { baseWhite, inkLighter, primaryBase, secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { SideSectionInfoItem } from '../../../initiative-list/side-section/side-section-info-item';
import { getCompletionPercentage, getEpicInfo } from './side-section-epics.helpers';

type Props = {
  epic: Epic | null;
  selectedDate?: Date | null;
  onClickRelatedTasks: () => void;
  onClose: () => void;
};

export const SideSectionEpics = ({ epic, selectedDate, onClickRelatedTasks, onClose }: Props) => {
  const i18n = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  if (!epic) {
    return null;
  }

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Group
          gap={4}
          onClick={() => window.open(epic.url, '_blank', 'noopener, noreferrer')}
          style={{ cursor: 'pointer' }}
        >
          <Text weight="bold" size="medium" color={secondaryBase}>
            {epic.title}
          </Text>
          <Tooltip variant="white" label={i18n.t('common.visit_source_page')} position="right">
            <Icon name="arrow_outward" size={16} color={secondaryBase} pointer />
          </Tooltip>
        </Group>
        <Icon name="close" onClick={onClose} size={20} color={skyDark} aria-label="Close panel" />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0}>
        <Stack>
          <Text weight="medium" size="medium">
            {epic.name}
          </Text>
          <Divider />
          <Stack gap={8}>
            {selectedDate && (
              <SideSectionInfoItem
                label={i18n.t('strategy.initiative_performance.selected_date', 'Selected Date')}
                value={formatMonthDate(selectedDate)}
              />
            )}
            <SideSectionInfoItem
              label={i18n.t('strategy.initiative_performance.completed_tasks', {
                work_units: i18n.t('common.work_units'),
              })}
              value={`${epic.subtasks_completed_count} / ${epic.subtasks_count}`}
            />
            <Progress value={getCompletionPercentage(epic)} color={primaryBase} size={4} />
          </Stack>
          <Stack gap={8}>
            {getEpicInfo(epic, initiative, i18n.t, currencyCode).map((info) => (
              <SideSectionInfoItem key={info.label} label={info.label} value={info.value} />
            ))}
          </Stack>
          {epic.description && (
            <>
              <Divider />
              <Text size="small" color={inkLighter}>
                {i18n.t('common.description', 'Description')}
              </Text>
              <Text size="small">{epic.description}</Text>
            </>
          )}
        </Stack>
      </ScrollArea>
      <Button
        variant="secondary"
        size="xs"
        radius="xl"
        fullWidth
        style={{ flexShrink: 0 }}
        leftSection={<Icon name="remove_red_eye" variant="filled" color={baseWhite} size={14} pointer />}
        onClick={onClickRelatedTasks}
      >
        <Text size="small" color={baseWhite}>
          {i18n.t('strategy.initiative_performance.related_tasks.title', {
            work_units: i18n.t('common.work_units'),
          })}
        </Text>
      </Button>
    </Stack>
  );
};
